<template>
  <v-container>
    <!-- Filters Section -->
    <v-row>
      <v-col cols="12">
        <v-card class="my-4">
          <v-card-text>
            <v-row align="center">
              <!-- Text Search Filter -->
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="searchText"
                  :label="$t('organisationDashboard.filters.search')"
                  :placeholder="$t('organisationDashboard.filters.searchPlaceholder')"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  outlined
                  dense
                  hide-details
                />
              </v-col>

              <!-- Date Range - Start Date -->
              <v-col cols="12" md="2">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="cursor-pointer">
                      <v-text-field
                        v-model="startDateDisplay"
                        :label="$t('organisationDashboard.filters.fromDate')"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        clearable
                        outlined
                        dense
                        hide-details
                      />
                    </div>
                  </template>
                  <v-date-picker
                    v-model="startDate"
                    no-title
                    scrollable
                    @input="startDateMenu = false"
                  />
                </v-menu>
              </v-col>

              <!-- Date Range - End Date -->
              <v-col cols="12" md="2">
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="cursor-pointer">
                      <v-text-field
                        v-model="endDateDisplay"
                        :label="$t('organisationDashboard.filters.toDate')"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        clearable
                        outlined
                        dense
                        hide-details
                      />
                    </div>
                  </template>
                  <v-date-picker
                    v-model="endDate"
                    no-title
                    scrollable
                    @input="endDateMenu = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="12" md class="d-flex justify-end align-center">
                <span
                  class="mr-2"
                  v-tooltip:bottom="$t('organisationDashboard.filters.saveConfigurationTooltip')"
                >
                  <platform-button
                    secondary
                    icon="filter"
                    @click="showSaveConfigurationModal = true"
                  >
                    {{ $t('organisationDashboard.filters.saveConfiguration') }}
                  </platform-button>
                </span>
                <span
                  class="mr-2"
                  v-tooltip:bottom="$t('organisationDashboard.filters.clearAllTooltip')"
                >
                  <platform-button
                    warning
                    icon="filter-off"
                    :disabled="!hasActiveFilters"
                    @click="clearAllFilters"
                  />
                </span>
                <platform-button
                  secondary
                  icon="refresh"
                  v-tooltip:bottom="$t('organisationDashboard.filters.refreshTooltip')"
                  :loading="loading"
                  @click="refreshAll"
                />
              </v-col>
            </v-row>

            <v-row align="center" class="mt-3">
              <!-- Company Filter -->
              <v-col cols="12" md="3">
                <v-select
                  ref="companyFilter"
                  v-model="selectedCompanyFilter"
                  :items="companyFilterOptions"
                  item-text="name"
                  item-value="id"
                  :label="$t('shared.entities.company')"
                  :placeholder="$t('organisationDashboard.filters.allCompanies')"
                  clearable
                  multiple
                  outlined
                  dense
                  hide-details
                  class="cursor-pointer"
                  :menu-props="{ contentClass: 'company-filter-menu', closeOnContentClick: false }"
                >
                  <template v-slot:selection="{ item, index }">
                    <div v-if="index === 0" class="d-flex align-center filter-selection">
                      <v-avatar size="24" class="mr-2 flex-shrink-0">
                        <file-viewer
                          object-type="companyLogo"
                          :object-id="item.id"
                          :company-id="item.id"
                          :placeholder="item.name ? item.name.charAt(0).toUpperCase() : '?'"
                          placeholder-size="xs"
                        />
                      </v-avatar>
                      <span class="text-truncate">{{ item.name }}</span>
                    </div>
                    <span v-if="index === 1" class="grey--text text-caption ml-1 flex-shrink-0">
                      {{ $t('organisationDashboard.filters.moreSelected', [selectedCompanyFilter.length - 1]) }}
                    </span>
                  </template>
                  <template v-slot:item="{ item, on, attrs }">
                    <v-list-item v-on="on" v-bind="attrs">
                      <div class="d-flex align-center">
                        <v-avatar size="28" class="mr-2">
                          <file-viewer
                            object-type="companyLogo"
                            :object-id="item.id"
                            :company-id="item.id"
                            :placeholder="item.name ? item.name.charAt(0).toUpperCase() : '?'"
                            placeholder-size="xs"
                          />
                        </v-avatar>
                        <span>{{ item.name }}</span>
                      </div>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>

              <!-- Consultant Filter -->
              <v-col cols="12" md="3">
                <v-select
                  ref="consultantFilter"
                  v-model="selectedConsultantFilter"
                  :items="consultantFilterOptions"
                  item-text="displayName"
                  item-value="cognitoIdentityId"
                  :label="$t('organisationDashboard.filters.consultant')"
                  :placeholder="$t('organisationDashboard.filters.allConsultants')"
                  clearable
                  multiple
                  outlined
                  dense
                  hide-details
                  class="cursor-pointer"
                  :menu-props="{ contentClass: 'consultant-filter-menu', closeOnContentClick: false }"
                >
                  <template v-slot:selection="{ item, index }">
                    <div v-if="index === 0" class="d-flex align-center filter-selection">
                      <v-avatar size="24" class="mr-2 flex-shrink-0">
                        <file-viewer
                          object-type="platformUserAvatar"
                          :object-id="item.cognitoIdentityId"
                          :placeholder="item.displayName ? item.displayName.charAt(0).toUpperCase() : '?'"
                          placeholder-size="xs"
                        />
                      </v-avatar>
                      <span class="text-truncate">{{ item.displayName }}</span>
                    </div>
                    <span v-if="index === 1" class="grey--text text-caption ml-1 flex-shrink-0">
                      {{ $t('organisationDashboard.filters.moreSelected', [selectedConsultantFilter.length - 1]) }}
                    </span>
                  </template>
                  <template v-slot:item="{ item, on, attrs }">
                    <v-list-item v-on="on" v-bind="attrs">
                      <div class="d-flex align-center">
                        <v-avatar size="28" class="mr-2">
                          <file-viewer
                            object-type="platformUserAvatar"
                            :object-id="item.cognitoIdentityId"
                            :placeholder="item.displayName ? item.displayName.charAt(0).toUpperCase() : '?'"
                            placeholder-size="xs"
                          />
                        </v-avatar>
                        <span>{{ item.displayName }}</span>
                      </div>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>

              <!-- Sector Filter -->
              <v-col cols="12" md="3">
                <v-select
                  ref="sectorFilter"
                  v-model="selectedSectorFilter"
                  :items="sectorFilterOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('form.company.sector')"
                  :placeholder="$t('organisationDashboard.filters.allSectors')"
                  clearable
                  multiple
                  outlined
                  dense
                  hide-details
                  class="cursor-pointer"
                  :menu-props="{ contentClass: 'sector-filter-menu', closeOnContentClick: false }"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" small outlined class="filter-selection">
                      {{ item.text }}
                    </v-chip>
                    <span v-if="index === 1" class="grey--text text-caption ml-1 flex-shrink-0">
                      {{ $t('organisationDashboard.filters.moreSelected', [selectedSectorFilter.length - 1]) }}
                    </span>
                  </template>
                  <template v-slot:item="{ item, on, attrs }">
                    <v-list-item v-on="on" v-bind="attrs">
                      <v-chip small outlined>
                        {{ item.text }}
                      </v-chip>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>

              <!-- Status Filter -->
              <v-col cols="12" md="3">
                <v-select
                  ref="statusFilter"
                  v-model="selectedStatusFilter"
                  :items="statusFilterOptions"
                  :label="$t('organisationDashboard.filters.status')"
                  :placeholder="$t('organisationDashboard.filters.allStatuses')"
                  clearable
                  multiple
                  outlined
                  dense
                  hide-details
                  class="cursor-pointer"
                  :menu-props="{ contentClass: 'status-filter-menu', closeOnContentClick: false }"
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip
                      v-if="index === 0"
                      small
                      :color="getStatusColor({ status: item })"
                      :text-color="getStatusTextColor({ status: item })"
                    >
                      {{ getTranslatedStatus(item) }}
                    </v-chip>
                    <span v-if="index === 1" class="grey--text text-caption ml-1 flex-shrink-0">
                      {{ $t('organisationDashboard.filters.moreSelected', [selectedStatusFilter.length - 1]) }}
                    </span>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-chip
                      small
                      :color="getStatusColor({ status: item })"
                      :text-color="getStatusTextColor({ status: item })"
                    >
                      {{ getTranslatedStatus(item) }}
                    </v-chip>
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-data-table
            :headers="tableHeaders"
            :items="filteredData"
            :loading="loading"
            :options.sync="tableOptions"
            :server-items-length="totalCount"
            class="elevation-1"
            :footer-props="{
              'items-per-page-options': [10, 25, 50, 100]
            }"
            @update:options="onOptionsUpdate"
          >
            <template v-slot:top>
              <v-data-footer
                :options.sync="tableOptions"
                :pagination="tablePagination"
                :items-per-page-options="[10, 25, 50, 100]"
                items-per-page-text="Rows per page:"
                @update:options="onOptionsUpdate"
              />
            </template>
            <template v-slot:[`item.companyName`]="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="40" class="mr-3">
                  <file-viewer
                    object-type="companyLogo"
                    :object-id="item.companyId"
                    :company-id="item.companyId"
                    :placeholder="getCompanyInitial(item)"
                    placeholder-size="sm"
                  />
                </v-avatar>
                <span>{{ item.companyName || $t('organisationDashboard.table.notAvailable') }}</span>
              </div>
            </template>

            <template v-slot:[`item.companySector`]="{ item }">
              <v-chip
                v-if="item.companySector"
                small
                outlined
              >
                {{ formatSectorName(item.companySector) }}
              </v-chip>
              <span v-else class="grey--text">—</span>
            </template>

            <template v-slot:[`item.initiatingConsultants`]="{ item }">
              <div v-if="getInitiatingConsultants(item).length > 0" class="d-flex flex-wrap justify-center align-center">
                <v-chip
                  v-for="consultant in getInitiatingConsultants(item)"
                  :key="consultant.cognitoIdentityId"
                  small
                  color="primary"
                  class="ma-1"
                >
                  {{ consultant.displayName || $t('organisationDashboard.table.unknown') }}
                </v-chip>
              </div>
              <div v-else class="text-center">
                <span class="grey--text">—</span>
              </div>
            </template>

            <template v-slot:[`item.date`]="{ item }">
              <span v-if="getFormattedStatusDate(item)">
                {{ getFormattedStatusDate(item) }}
              </span>
              <span v-else class="grey--text">—</span>
            </template>

            <template v-slot:[`item.status`]="{ item }">
              <v-chip
                small
                :color="getStatusColor(item)"
                :text-color="getStatusTextColor(item)"
              >
                {{ getStatusLabel(item) }}
              </v-chip>
            </template>

            <template v-slot:no-data>
              <div class="text-center py-4">
                <v-icon large color="grey lighten-1" class="mb-2">mdi-clipboard-text-outline</v-icon>
                <p class="grey--text">{{ $t('organisationDashboard.table.noData') }}</p>
              </div>
            </template>

            <template v-slot:loading>
              <div class="text-center py-4">
                <v-progress-circular indeterminate color="primary" />
                <p class="grey--text mt-2">{{ $t('organisationDashboard.table.loading') }}</p>
              </div>
            </template>

            <template v-slot:[`footer.prepend`]>
              <v-progress-linear
                v-if="loading"
                absolute
                indeterminate
                color="primary"
                height="4"
                class="footer-loading-bar"
              />
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <save-dashboard-configuration-modal
      v-model="showSaveConfigurationModal"
      :filter-state="currentFilterState"
      :table-options="tableOptions"
      @saved="showSaveConfigurationModal = false"
      @reset="resetFiltersToDefault"
      @close="showSaveConfigurationModal = false"
    />
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue'
import dayjs from 'dayjs'
import API from '@/services/API'
import FileViewer from '@/components/FileViewer.vue'
import PlatformButton from '@/components/shared/PlatformButton.vue'
import SaveDashboardConfigurationModal from '@/components/organisation/SaveDashboardConfigurationModal.vue'
import { addSpacesToPascalCase } from '@betterboards/shared/helpers/util/strings'
import {
  DashboardConfiguration,
  DEFAULT_DASHBOARD_CONFIGURATION,
  FilterState
} from '@betterboards/shared/types/OrganisationUser'

enum QuestionnaireStatus {
  Created = 'Created',
  PartialReleased = 'Partial Released',
  Released = 'Released',
  Completed = 'Completed'
}

interface ConsultantInfo {
  cognitoIdentityId: string
  displayName: string | null
}

interface QuestionnaireInfo {
  questionnaireId: string
  questionnaireName: string | null
  companyId: string
  companyName: string | null
  companySector: string | null
  createdAt: string | null
  releasedAt: string | null
  dispatchedAt: string | null
  status: QuestionnaireStatus
  initiatingConsultants: ConsultantInfo[]
  formerInitiatingConsultants: ConsultantInfo[]
}

interface MetricsResponse {
  metrics: unknown[]
  questionnaireInfo: QuestionnaireInfo[]
  totalCount: number
  page: number
  pageSize: number
}

interface CompanyInfo {
  id: string
  name: string
}

interface SectorInfo {
  value: string
  text: string
}

interface FilterOptionsResponse {
  consultants: ConsultantInfo[]
  companies: CompanyInfo[]
  sectors: SectorInfo[]
}

interface TableOptions {
  page: number
  itemsPerPage: number
  sortBy: string[]
  sortDesc: boolean[]
}

export default Vue.extend({
  name: 'MetricsDashboard',
  components: {
    FileViewer,
    PlatformButton,
    SaveDashboardConfigurationModal
  },
  data: () => ({
    loading: false,
    questionnaireInfo: [] as QuestionnaireInfo[],
    consultants: [] as ConsultantInfo[],
    companies: [] as CompanyInfo[],
    sectors: [] as SectorInfo[],
    totalCount: 0,
    searchText: '',
    searchDebounceTimer: null as ReturnType<typeof setTimeout> | null,
    pendingRequest: null as Promise<any> | null,
    requestCounter: 0,
    pageBeforeFilter: 1,
    selectedStatusFilter: [] as QuestionnaireStatus[],
    statusFilterOptions: [QuestionnaireStatus.Completed, QuestionnaireStatus.Released, QuestionnaireStatus.PartialReleased, QuestionnaireStatus.Created],
    selectedCompanyFilter: [] as string[],
    selectedSectorFilter: [] as string[],
    selectedConsultantFilter: [] as string[],
    startDate: dayjs().subtract(3, 'month').format('YYYY-MM-DD') as string | null,
    endDate: dayjs().format('YYYY-MM-DD') as string | null,
    startDateMenu: false,
    endDateMenu: false,
    initialFetchDone: false,
    tableOptions: {
      page: 1,
      itemsPerPage: 25,
      sortBy: ['date'] as string[],
      sortDesc: [true] as boolean[]
    } as TableOptions,
    filterMenuObserver: null as MutationObserver | null,
    showSaveConfigurationModal: false,
    configurationLoaded: false
  }),
  mounted (): void {
    void this.loadDashboardConfiguration().then(() => {
      void this.fetchFilterOptions()
      void this.fetchMetrics().then(() => {
        this.initialFetchDone = true
      })
    })
    this.setupFilterMenuListeners()
  },
  beforeDestroy (): void {
    this.removeFilterMenuListeners()
  },
  watch: {
    selectedCompanyFilter (): void {
      if (this.initialFetchDone) {
        this.handleFilterChange()
        void this.fetchMetrics()
      }
    },
    selectedSectorFilter (): void {
      if (this.initialFetchDone) {
        this.handleFilterChange()
        void this.fetchMetrics()
      }
    },
    selectedConsultantFilter (): void {
      if (this.initialFetchDone) {
        this.handleFilterChange()
        void this.fetchMetrics()
      }
    },
    selectedStatusFilter (): void {
      if (this.initialFetchDone) {
        this.handleFilterChange()
        void this.fetchMetrics()
      }
    },
    startDate (): void {
      if (this.initialFetchDone) {
        this.handleFilterChange()
        void this.fetchMetrics()
      }
    },
    endDate (): void {
      if (this.initialFetchDone) {
        this.handleFilterChange()
        void this.fetchMetrics()
      }
    },
    searchText (): void {
      if (this.initialFetchDone) {
        // Debounce search - wait for user to stop typing
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer)
        }
        this.searchDebounceTimer = setTimeout(() => {
          this.handleFilterChange()
          void this.fetchMetrics()
        }, 800)
      }
    }
  },
  computed: {
    startDateDisplay: {
      get (): string | null {
        if (!this.startDate) {
          return null
        }
        return dayjs(this.startDate).format('DD MMM YYYY')
      },
      set (value: string | null): void {
        if (!value) {
          this.startDate = null
        }
      }
    },
    endDateDisplay: {
      get (): string | null {
        if (!this.endDate) {
          return null
        }
        return dayjs(this.endDate).format('DD MMM YYYY')
      },
      set (value: string | null): void {
        if (!value) {
          this.endDate = null
        }
      }
    },
    companyFilterOptions (): CompanyInfo[] {
      return this.companies
    },
    sectorFilterOptions (): SectorInfo[] {
      return this.sectors
    },
    consultantFilterOptions (): ConsultantInfo[] {
      return this.consultants
    },
    filteredData (): QuestionnaireInfo[] {
      return this.questionnaireInfo
    },
    hasActiveFilters (): boolean {
      return !!(this.selectedCompanyFilter.length > 0 || this.selectedSectorFilter.length > 0 || this.selectedStatusFilter.length > 0 || this.selectedConsultantFilter.length > 0 || this.startDate || this.endDate || this.searchText)
    },
    currentFilterState (): FilterState {
      return {
        selectedCompanyFilter: this.selectedCompanyFilter,
        selectedSectorFilter: this.selectedSectorFilter,
        selectedConsultantFilter: this.selectedConsultantFilter,
        selectedStatusFilter: this.selectedStatusFilter as string[],
        startDate: this.startDate,
        endDate: this.endDate,
        searchText: this.searchText
      }
    },
    tableHeaders (): Array<{ text: string, value: string, sortable: boolean, align?: string }> {
      return [
        {
          text: this.$t('shared.entities.company') as string,
          value: 'companyName',
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('organisationDashboard.table.headers.initiatingConsultants') as string,
          value: 'initiatingConsultants',
          sortable: false,
          align: 'center'
        },
        {
          text: this.$t('organisationDashboard.table.headers.questionnaireName') as string,
          value: 'questionnaireName',
          sortable: true
        },
        {
          text: this.$t('form.company.sector') as string,
          value: 'companySector',
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('organisationDashboard.table.headers.date') as string,
          value: 'date',
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('organisationDashboard.table.headers.status') as string,
          value: 'status',
          sortable: true,
          align: 'center'
        }
      ]
    },
    tablePagination (): { page: number, itemsPerPage: number, pageStart: number, pageStop: number, pageCount: number, itemsLength: number } {
      const { page, itemsPerPage } = this.tableOptions
      const pageStart = (page - 1) * itemsPerPage
      const pageStop = Math.min(page * itemsPerPage, this.totalCount)
      const pageCount = itemsPerPage <= 0 ? 1 : Math.ceil(this.totalCount / itemsPerPage)
      return {
        page,
        itemsPerPage,
        pageStart,
        pageStop,
        pageCount,
        itemsLength: this.totalCount
      }
    }
  },
  methods: {
    handleFilterChange (): void {
      if (this.hasActiveFilters) {
        this.tableOptions.page = 1
      } else {
        this.tableOptions.page = this.pageBeforeFilter
      }
    },
    clearAllFilters (): void {
      this.searchText = ''
      this.selectedCompanyFilter = []
      this.selectedConsultantFilter = []
      this.selectedSectorFilter = []
      this.selectedStatusFilter = []
      this.startDate = null
      this.endDate = null
    },
    resetFiltersToDefault (): void {
      this.applyDashboardConfiguration(DEFAULT_DASHBOARD_CONFIGURATION)
      this.showSaveConfigurationModal = false
    },
    setupFilterMenuListeners (): void {
      const filterMenuConfig: Record<string, string> = {
        'company-filter-menu': 'companyFilter',
        'consultant-filter-menu': 'consultantFilter',
        'sector-filter-menu': 'sectorFilter',
        'status-filter-menu': 'statusFilter'
      }

      // Use MutationObserver to detect when any filter menu appears
      this.filterMenuObserver = new MutationObserver((mutations: MutationRecord[]) => {
        for (const mutation of mutations) {
          for (const node of Array.from(mutation.addedNodes)) {
            if (node instanceof HTMLElement) {
              for (const [menuClass, refName] of Object.entries(filterMenuConfig)) {
                if (node.classList.contains(menuClass)) {
                  node.addEventListener('mouseleave', () => this.closeFilter(refName))
                }
              }
            }
          }
        }
      })
      this.filterMenuObserver.observe(document.body, { childList: true, subtree: true })
    },
    removeFilterMenuListeners (): void {
      if (this.filterMenuObserver) {
        this.filterMenuObserver.disconnect()
      }
    },
    closeFilter (refName: string): void {
      const filter = this.$refs[refName] as any
      if (filter) {
        filter.blur()
      }
    },
    getCompanyInitial (item: QuestionnaireInfo): string {
      if (item.companyName) {
        return item.companyName.charAt(0).toUpperCase()
      }
      return this.$t('organisationDashboard.table.notAvailable') as string
    },
    formatSectorName (sector: string): string {
      return addSpacesToPascalCase(sector)
    },
    getStatusLabel (item: QuestionnaireInfo): string {
      return this.getTranslatedStatus(item.status)
    },
    getTranslatedStatus (status: QuestionnaireStatus | string): string {
      switch (status) {
        case QuestionnaireStatus.Completed:
          return this.$t('organisationDashboard.status.completed') as string
        case QuestionnaireStatus.Created:
          return this.$t('organisationDashboard.status.created') as string
        case QuestionnaireStatus.PartialReleased:
          return this.$t('organisationDashboard.status.partialReleased') as string
        case QuestionnaireStatus.Released:
          return this.$t('organisationDashboard.status.released') as string
        default:
          return status
      }
    },
    getStatusColor (item: Pick<QuestionnaireInfo, 'status'>): string {
      switch (item.status) {
        case QuestionnaireStatus.Completed:
          return 'purple'
        case QuestionnaireStatus.Released:
          return 'success'
        case QuestionnaireStatus.PartialReleased:
          return 'amber'
        default:
          return 'blue'
      }
    },
    getStatusTextColor (item: Pick<QuestionnaireInfo, 'status'>): string {
      switch (item.status) {
        case QuestionnaireStatus.Completed:
          return 'white'
        case QuestionnaireStatus.Released:
          return 'white'
        case QuestionnaireStatus.PartialReleased:
          return 'black'
        default:
          return 'white'
      }
    },
    getStatusDate (item: QuestionnaireInfo): string | null {
      if (item.dispatchedAt) {
        return item.dispatchedAt
      }
      if (item.releasedAt) {
        return item.releasedAt
      }
      return item.createdAt
    },
    formatDate (dateString: string): string {
      return dayjs(dateString).format('DD MMM YYYY')
    },
    getFormattedStatusDate (item: QuestionnaireInfo): string | null {
      const date = this.getStatusDate(item)
      return date ? this.formatDate(date) : null
    },
    getInitiatingConsultants (item: QuestionnaireInfo): ConsultantInfo[] {
      return item.initiatingConsultants || []
    },
    onOptionsUpdate (): void {
      // Only fetch if initial fetch is done (prevents double-fetch on mount)
      if (this.initialFetchDone) {
        // Save page when navigating without filters
        if (!this.hasActiveFilters) {
          this.pageBeforeFilter = this.tableOptions.page
        }
        void this.fetchMetrics()
      }
    },
    async fetchMetrics (): Promise<void> {
      this.requestCounter++
      const currentRequestId = this.requestCounter

      if (this.pendingRequest) {
        await this.pendingRequest
      }

      this.loading = true

      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions

        const requestPromise: Promise<MetricsResponse> = API.post('backendfunctions', '/organisation/dashboard', {
          body: {
            action: 'getMetrics',
            page,
            pageSize: itemsPerPage,
            sortBy: sortBy.length > 0 ? sortBy[0] : undefined,
            sortDesc: sortDesc.length > 0 ? sortDesc[0] : undefined,
            companyFilter: this.selectedCompanyFilter.length > 0 ? this.selectedCompanyFilter : undefined,
            sectorFilter: this.selectedSectorFilter.length > 0 ? this.selectedSectorFilter : undefined,
            consultantFilter: this.selectedConsultantFilter.length > 0 ? this.selectedConsultantFilter : undefined,
            statusFilter: this.selectedStatusFilter.length > 0 ? this.selectedStatusFilter : undefined,
            startDate: this.startDate || undefined,
            endDate: this.endDate || undefined,
            searchText: this.searchText || undefined
          }
        })

        this.pendingRequest = requestPromise
        const response = await requestPromise

        if (currentRequestId === this.requestCounter) {
          this.questionnaireInfo = response.questionnaireInfo ?? []
          this.totalCount = response.totalCount ?? this.questionnaireInfo.length
          console.info(`Fetched ${this.questionnaireInfo.length} of ${this.totalCount} questionnaires`)
        } else {
          console.debug('Discarding stale request response')
        }
      } catch (error: any) {
        console.error('Failed to fetch organisation metrics:', error)
        this.$toasted.error('Failed to load metrics data')
      } finally {
        if (currentRequestId === this.requestCounter) {
          this.loading = false
          this.pendingRequest = null
        }
      }
    },
    async fetchFilterOptions (): Promise<void> {
      try {
        const response: FilterOptionsResponse = await API.post('backendfunctions', '/organisation/dashboard', {
          body: {
            action: 'getFilterOptions'
          }
        })

        this.consultants = response.consultants ?? []
        this.companies = response.companies ?? []
        this.sectors = response.sectors ?? []
        console.info(`Fetched filter options: ${this.consultants.length} consultants, ${this.companies.length} companies, ${this.sectors.length} sectors`)
      } catch (error: any) {
        console.error('Failed to fetch filter options:', error)
      }
    },
    async refreshAll (): Promise<void> {
      await Promise.all([
        this.fetchFilterOptions(),
        this.fetchMetrics()
      ])
    },
    async loadDashboardConfiguration (): Promise<void> {
      try {
        const response = await API.post('backendfunctions', '/organisation/dashboard', {
          body: {
            action: 'getDashboardConfiguration'
          }
        }) as { dashboardConfiguration: DashboardConfiguration, isDefault: boolean }

        const config = response.dashboardConfiguration

        // Apply loaded configuration
        this.applyDashboardConfiguration(config)

        console.info('Loaded dashboard configuration:', {
          isDefault: response.isDefault,
          config
        })
      } catch (error: any) {
        console.error('Failed to load dashboard configuration:', error)
        // Use defaults on error
        this.applyDashboardConfiguration(DEFAULT_DASHBOARD_CONFIGURATION)
      } finally {
        this.configurationLoaded = true
      }
    },
    applyDashboardConfiguration (config: DashboardConfiguration): void {
      const { filters, tableOptions } = config
      const today = dayjs()

      this.selectedCompanyFilter = filters.selectedCompanyFilter ?? []
      this.selectedSectorFilter = filters.selectedSectorFilter ?? []
      this.selectedConsultantFilter = filters.selectedConsultantFilter ?? []
      this.selectedStatusFilter = (filters.selectedStatusFilter ?? DEFAULT_DASHBOARD_CONFIGURATION.filters.selectedStatusFilter) as QuestionnaireStatus[]
      this.searchText = filters.searchText ?? ''

      switch (filters.dateRange.type) {
        case 'relative':
          if (filters.dateRange.startDaysFromToday !== null && filters.dateRange.startDaysFromToday !== undefined) {
            this.startDate = today.add(filters.dateRange.startDaysFromToday, 'day').format('YYYY-MM-DD')
          }
          if (filters.dateRange.endDaysFromToday !== null && filters.dateRange.endDaysFromToday !== undefined) {
            this.endDate = today.add(filters.dateRange.endDaysFromToday, 'day').format('YYYY-MM-DD')
          }
          break
        case 'absolute':
          this.startDate = filters.dateRange.startDate ?? null
          this.endDate = filters.dateRange.endDate ?? null
          break
      }

      this.tableOptions = {
        ...this.tableOptions,
        itemsPerPage: tableOptions.itemsPerPage ?? 25,
        sortBy: tableOptions.sortBy ?? ['date'],
        sortDesc: tableOptions.sortDesc ?? [true]
      }
    }
  }
})
</script>

<style lang="scss" scoped>
::v-deep .v-data-table {
  thead th {
    text-align: center !important;
    border-right: 1px solid rgba(0, 0, 0, 0.12) !important;

    &:last-child {
      border-right: none !important;
    }
  }

  tbody td {
    border-right: 1px solid rgba(0, 0, 0, 0.12) !important;

    &:last-child {
      border-right: none !important;
    }
  }
}

::v-deep .cursor-pointer,
::v-deep .cursor-pointer * {
  cursor: pointer !important;
}

// Filter selection text overflow handling
::v-deep .v-select {
  .v-select__selections {
    overflow: hidden;
    flex-wrap: nowrap;

    .filter-selection {
      overflow: hidden;
      min-width: 0;
      flex: 1;
    }

    .filter-selection-text {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

::v-deep .v-data-footer {
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
  position: relative;
}

::v-deep .footer-loading-bar {
  top: 0;
}
</style>
